import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineArrowLeft,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineUser,
  HiOutlineClipboardDocumentCheck,
} from "react-icons/hi2";
import {
  authService,
  capaService,
  masterDataService,
  userService,
} from "../services";
import { usePermissions } from "../hooks/usePermissions";
import SharedModal from "../components/common/SharedModal";
import PageLoading from "../components/common/PageLoading";
import "./CAPATaskManagement.css";

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedCAPATaskData = () => {
  try {
    const cachedTasks = sessionStorage.getItem("capa_tasks_data");
    const cachedCapaRecords = sessionStorage.getItem("capa_records_data");
    const cachedStatuses = sessionStorage.getItem(
      "capa_task_management_statuses_data",
    );
    const cachedUsers = sessionStorage.getItem("users_data");

    return {
      tasks: cachedTasks ? JSON.parse(cachedTasks) : [],
      capaRecords: cachedCapaRecords ? JSON.parse(cachedCapaRecords) : [],
      statuses: cachedStatuses ? JSON.parse(cachedStatuses) : [],
      users: cachedUsers ? JSON.parse(cachedUsers) : [],
      hasAll: !!(
        cachedTasks &&
        cachedCapaRecords &&
        cachedStatuses &&
        cachedUsers
      ),
    };
  } catch (e) {
    return {
      tasks: [],
      capaRecords: [],
      statuses: [],
      users: [],
      hasAll: false,
    };
  }
};

const CAPATaskManagement = () => {
  const navigate = useNavigate();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("CAPA.View");
  const canCreate = hasPermission("CAPA.Create");
  const canEdit = hasPermission("CAPA.Edit");
  const canDelete = hasPermission("CAPA.Delete");
  // Allow access if user can view OR create actions
  const canAccess = canView || canCreate;

  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedCAPATaskData();
  const [loading, setLoading] = useState(!cachedData.hasAll);
  const [error, setError] = useState("");
  const [tasks, setTasks] = useState(cachedData.tasks);
  const [statuses, setStatuses] = useState(cachedData.statuses);
  const [users, setUsers] = useState(cachedData.users);
  const [capaRecords, setCapaRecords] = useState(cachedData.capaRecords);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedCapa, setSelectedCapa] = useState(null);

  const [taskFormData, setTaskFormData] = useState({
    taskDescription: "",
    assignedTo: "",
    dueDate: "",
    statusId: "",
    notes: "",
    priority: 3,
    capaId: "",
  });

  const [taskFormErrors, setTaskFormErrors] = useState({});
  const [submittingTask, setSubmittingTask] = useState(false);

  // Group tasks by status
  const taskColumns = {
    pending: { id: "pending", title: "Pending", tasks: [] },
    inProgress: { id: "inProgress", title: "In Progress", tasks: [] },
    completed: { id: "completed", title: "Completed", tasks: [] },
    cancelled: { id: "cancelled", title: "Cancelled", tasks: [] },
  };

  // Refresh cached data on mount (in case it was updated elsewhere)
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedCAPATaskData();
      if (refreshedCachedData.hasAll) {
        setTasks(refreshedCachedData.tasks);
        setCapaRecords(refreshedCachedData.capaRecords);
        setStatuses(refreshedCachedData.statuses);
        setUsers(refreshedCachedData.users);
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setError("Please login to manage tasks");
      setLoading(false);
      return;
    }

    const hasCachedData =
      sessionStorage.getItem("capa_tasks_data") &&
      sessionStorage.getItem("capa_records_data") &&
      sessionStorage.getItem("capa_task_management_statuses_data") &&
      sessionStorage.getItem("users_data");

    if (!canAccess) {
      setError(
        "You do not have permission to access CAPA actions. You need either CAPAAction.View or CAPAAction.Create permission.",
      );
      setLoading(false);
      return;
    }

    if (hasCachedData) {
      setLoading(false);
      loadInitialData();
      return;
    }

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, canAccess]);

  const loadInitialData = async () => {
    try {
      const hasCachedData = sessionStorage.getItem("capa_tasks_data");
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");

      const [allCapaData, statusesData, usersData, allTasksData] = await Promise.all([
        capaService.getCapaRecords(),
        masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
        userService.getAllUsers?.() || Promise.resolve([]),
        capaService.getAllCapaActions(),
      ]);

      const allCapaDataResult = allCapaData.data || allCapaData || [];
      setCapaRecords(allCapaDataResult);
      setUsers(usersData || []);

      const allStatuses = Array.isArray(statusesData) ? statusesData : [];

      const normalizeName = (name) =>
        name ? name.toLowerCase().replace(/\s/g, "") : "";

      const deduplicateStatuses = (statusList) => {
        const uniqueStatuses = [];
        const seenNames = new Set();
        statusList.forEach((status) => {
          const key = normalizeName(status.statusName);
          if (!seenNames.has(key)) {
            uniqueStatuses.push(status);
            seenNames.add(key);
          }
        });
        return uniqueStatuses;
      };

      const uniqueAllStatuses = deduplicateStatuses(allStatuses);

      let actionStatuses = allStatuses.filter(
        (s) =>
          !s.statusType ||
          s.statusType === "Action" ||
          s.statusType === null ||
          s.statusType === undefined,
      );
      actionStatuses = deduplicateStatuses(actionStatuses);

      const finalStatuses =
        actionStatuses.length > 0 ? actionStatuses : uniqueAllStatuses;
      setStatuses(finalStatuses);

      sessionStorage.setItem("capa_records_data", JSON.stringify(allCapaDataResult));
      sessionStorage.setItem("capa_task_management_statuses_data", JSON.stringify(finalStatuses));
      sessionStorage.setItem("users_data", JSON.stringify(usersData || []));

      const enrichedTasks = (allTasksData || []).map((task) => {
        const capa = allCapaDataResult.find((c) => c.capaId === task.capaId);
        return { ...task, capaTitle: capa ? capa.title : "Unknown CAPA" };
      });

      setTasks(enrichedTasks);
      sessionStorage.setItem("capa_tasks_data", JSON.stringify(enrichedTasks));
    } catch (err) {
      console.error("Error loading task management data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusName = (statusId) => {
    if (!statusId) return "-";
    const s = statuses.find((x) => x.statusId === statusId);
    return s ? s.statusName : `Status ${statusId}`;
  };

  const getStatusColumn = (statusName) => {
    const normalized = statusName?.toLowerCase() || "";
    if (normalized.includes("pending")) return "pending";
    if (normalized.includes("progress")) return "inProgress";
    if (normalized.includes("completed") || normalized.includes("complete")) return "completed";
    if (normalized.includes("cancelled") || normalized.includes("cancel")) return "cancelled";
    return "pending";
  };

  const getStatusIdForColumn = (columnId) => {
    const columnStatusMap = {
      pending: statuses.find((s) => s.statusName?.toLowerCase().includes("pending"))?.statusId,
      inProgress: statuses.find((s) => s.statusName?.toLowerCase().includes("progress"))?.statusId,
      completed: statuses.find(
        (s) =>
          s.statusName?.toLowerCase().includes("completed") ||
          s.statusName?.toLowerCase().includes("complete"),
      )?.statusId,
      cancelled: statuses.find(
        (s) =>
          s.statusName?.toLowerCase().includes("cancelled") ||
          s.statusName?.toLowerCase().includes("cancel"),
      )?.statusId,
    };
    return columnStatusMap[columnId];
  };

  const organizedTasks = tasks.reduce(
    (acc, task) => {
      const column = getStatusColumn(getStatusName(task.statusId));
      if (!acc[column]) acc[column] = [];
      acc[column].push(task);
      return acc;
    },
    { pending: [], inProgress: [], completed: [], cancelled: [] },
  );

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(task));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedTask(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnId);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverColumn(null);
  };

  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverColumn(null);

    if (!draggedTask) return;

    const newStatusId = getStatusIdForColumn(targetColumn);
    if (!newStatusId || draggedTask.statusId === newStatusId) {
      setDraggedTask(null);
      return;
    }

    try {
      await capaService.updateCapaActionStatus(draggedTask.actionId, newStatusId);
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.actionId === draggedTask.actionId
            ? { ...task, statusId: newStatusId }
            : task,
        ),
      );
      setDraggedTask(null);
    } catch (err) {
      console.error("Error updating task status:", err);
      setError("Failed to update task status. Please try again.");
      setDraggedTask(null);
    }
  };

  const handleUpdateTaskStatus = async (actionId, newStatusId) => {
    try {
      await capaService.updateCapaActionStatus(actionId, newStatusId);
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.actionId === actionId ? { ...task, statusId: newStatusId } : task,
        ),
      );
    } catch (err) {
      console.error("Error updating task status:", err);
      setError("Failed to update task status. Please try again.");
    }
  };

  const handleAddTaskClick = () => {
    if (!canCreate) {
      setError("You do not have permission to create CAPA actions");
      return;
    }
    setEditingTask(null);
    setSelectedCapa(null);
    setTaskFormData({
      taskDescription: "",
      assignedTo: "",
      dueDate: "",
      statusId: "",
      notes: "",
      priority: 3,
      capaId: "",
    });
    setTaskFormErrors({});
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    if (!canEdit) {
      setError("You do not have permission to edit CAPA actions");
      return;
    }
    setEditingTask(task);
    setSelectedCapa(capaRecords.find((c) => c.capaId === task.capaId));
    setTaskFormData({
      taskDescription: task.taskDescription || "",
      assignedTo: task.assignedTo?.toString() || "",
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split("T")[0] : "",
      statusId: task.statusId?.toString() || "",
      notes: task.notes || "",
      priority: task.priority || 3,
      capaId: task.capaId?.toString() || "",
    });
    setTaskFormErrors({});
    setShowTaskModal(true);
  };

  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setTaskFormData((prev) => ({ ...prev, [name]: value }));
    setTaskFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateTaskForm = () => {
    const errors = {};
    if (!taskFormData.taskDescription?.trim()) {
      errors.taskDescription = "Task description is required";
    }
    if (!taskFormData.statusId || taskFormData.statusId === "") {
      errors.statusId = "Status is required";
    }
    if (!taskFormData.capaId || taskFormData.capaId === "") {
      errors.capaId = "CAPA is required";
    }
    setTaskFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTask = async () => {
    if (editingTask && !canEdit) {
      setError("You do not have permission to edit CAPA actions");
      return;
    }
    if (!editingTask && !canCreate) {
      setError("You do not have permission to create CAPA actions");
      return;
    }
    if (!validateTaskForm()) return;

    try {
      setSubmittingTask(true);
      setError("");

      const taskStatusIdNum = parseInt(taskFormData.statusId, 10);
      if (isNaN(taskStatusIdNum) || taskStatusIdNum <= 0) {
        setTaskFormErrors({ statusId: "Please select a valid status" });
        setSubmittingTask(false);
        return;
      }

      const taskPayload = {
        capaId: parseInt(taskFormData.capaId, 10),
        taskDescription: taskFormData.taskDescription.trim(),
        assignedTo: taskFormData.assignedTo ? parseInt(taskFormData.assignedTo, 10) : null,
        dueDate: taskFormData.dueDate ? new Date(taskFormData.dueDate).toISOString() : null,
        statusId: taskStatusIdNum,
        notes: taskFormData.notes?.trim() || null,
        priority: parseInt(taskFormData.priority, 10),
      };

      if (editingTask) {
        taskPayload.actionId = editingTask.actionId;
        await capaService.updateCapaAction(editingTask.actionId, taskPayload);
      } else {
        await capaService.createCapaAction(taskPayload);
      }

      await loadInitialData();
      setShowTaskModal(false);
      setEditingTask(null);
      setSelectedCapa(null);
      setTaskFormData({
        taskDescription: "",
        assignedTo: "",
        dueDate: "",
        statusId: "",
        notes: "",
        priority: 3,
        capaId: "",
      });
    } catch (err) {
      console.error("Error saving task:", err);
      setError(err.response?.data?.message || "Failed to save task");
    } finally {
      setSubmittingTask(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!canDelete) {
      setError("You do not have permission to delete CAPA actions");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      await capaService.deleteCapaAction(taskId);
      setTasks((prevTasks) => prevTasks.filter((task) => task.actionId !== taskId));
    } catch (err) {
      console.error("Error deleting task:", err);
      setError("Failed to delete task");
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 1: return "High";
      case 2: return "Medium";
      case 3: return "Low";
      default: return "Low";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return "priority-high";
      case 2: return "priority-medium";
      case 3: return "priority-low";
      default: return "priority-low";
    }
  };

  const getUserName = (userId) => {
    if (!userId) return "-";
    const user = users.find((u) => u.userId === userId);
    return user ? user.userName || user.fullName || `User ${userId}` : `User ${userId}`;
  };

  const getCapaTitle = (capaId) => {
    if (!capaId) return "-";
    const capa = capaRecords.find((c) => c.capaId === capaId);
    return capa ? capa.title : `CAPA ${capaId}`;
  };

  // Column accent colour config — purely visual, no logic
  const columnConfig = {
    pending: { accentColor: "#F59E0B", countBg: "#FEF3C7", countColor: "#92400E", borderColor: "#F59E0B" },
    inProgress: { accentColor: "#4F8EF7", countBg: "#DBEAFE", countColor: "#1E40AF", borderColor: "#4F8EF7" },
    completed: { accentColor: "#22C55E", countBg: "#DCFCE7", countColor: "#166534", borderColor: "#22C55E" },
    cancelled: { accentColor: "#EF4444", countBg: "#FEE2E2", countColor: "#991B1B", borderColor: "#EF4444" },
  };

  const priorityBadgeStyle = (priority) => {
    switch (priority) {
      case 1: return { background: "#FEE2E2", color: "#B91C1C" };
      case 2: return { background: "#FEF9C3", color: "#A16207" };
      case 3: return { background: "#DCFCE7", color: "#15803D" };
      default: return { background: "#DCFCE7", color: "#15803D" };
    }
  };

  if (loading) {
    return <PageLoading message="Loading tasks..." />;
  }

  return (
    <div className="pagecontainer capa-task-management-page">
      <div className="page-header">
        <div className="header-content">
          <div className="d-flex">
            <button
              className="oee-report-back-btn mr-2"
              onClick={() => navigate("/home/capa")}
            >
              <HiOutlineArrowLeft />
            </button>
            <div>
              <h1>Task Management</h1>
              <p>Manage CAPA tasks with drag-and-drop functionality</p>
            </div>
          </div>
        </div>
        <div className="header-actions">
          {canCreate && (
            <button className="btn-primary" onClick={handleAddTaskClick}>
              <HiOutlinePlus />
              Add Task
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* ── TASK BOARD ── only this section is redesigned ── */}
      <div
        className="task-board"
      >
        {Object.values(taskColumns).map((column) => {
          const cfg = columnConfig[column.id];
          const colTasks = organizedTasks[column.id] || [];
          const isDragOver = dragOverColumn === column.id;

          return (
            <div
              key={column.id}
              className={`task-column ${isDragOver ? "drag-over" : ""}`}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
              style={{
                display: "flex",
                flexDirection: "column",
                background: isDragOver ? "#F0F5FE" : "#F8F9FC",
                border: isDragOver
                  ? `2px dashed ${cfg.accentColor}`
                  : "1px solid #E8ECF2",
                borderRadius: "12px",
                overflow: "hidden",
                transition: "border 0.15s, background 0.15s",
              }}
            >
              {/* Column Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 16px 12px",
                  borderBottom: `2px solid ${cfg.accentColor}`,
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "11px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#5A6478",
                    }}
                  >
                    {column.title}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: cfg.countBg,
                      color: cfg.countColor,
                    }}
                  >
                    {colTasks.length}
                  </span>
                </div>
              </div>

              {/* Task List */}
              <div
              // style={{
              //   display: "flex",
              //   flexDirection: "column",
              //   gap: "10px",
              //   padding: "12px 12px 14px",
              //   minHeight: "80px",
              // }}
              >
                {colTasks.length > 0 ? (
                  colTasks.map((task) => (
                    <div
                      key={task.actionId}
                      className={`task-card ${draggedTask?.actionId === task.actionId ? "dragging" : ""}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      style={{
                        background: "#fff",
                        border: "1px solid #E8ECF2",
                        borderRadius: "0px",
                        padding: "14px 14px 12px",
                        cursor: "grab",
                        position: "relative",
                        transition: "box-shadow 0.18s, border-color 0.18s",
                        borderLeft: `3px solid ${cfg.accentColor}`,
                        opacity: draggedTask?.actionId === task.actionId ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        // e.currentTarget.style.boxShadow = "0 4px 16px rgba(79,142,247,0.10)";
                        // e.currentTarget.style.borderColor = "#C6D3E8";
                        e.currentTarget.querySelector(".card-action-btns").style.opacity = "1";
                      }}
                      onMouseLeave={(e) => {
                        // e.currentTarget.style.boxShadow = "none";
                        // e.currentTarget.style.borderColor = "#E8ECF2";
                        e.currentTarget.querySelector(".card-action-btns").style.opacity = "0";
                      }}
                    >
                      {/* Card Header Row */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          marginBottom: "10px", width: "100%"
                        }}
                      >
                        <strong
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#1A1D23",
                            lineHeight: "1.4",
                            flex: 1,
                            paddingRight: "8px",
                          }}
                        >
                          {task.taskDescription}
                        </strong>

                        {/* Action buttons — hidden until hover via onMouseEnter */}
                        <div
                          className="card-action-btns"
                          style={{
                            display: "flex",
                            gap: "4px",
                            opacity: "0",
                            transition: "opacity 0.15s",
                            flexShrink: 0,
                          }}
                        >
                          {canEdit && (
                            <button
                              className="btn-icon-edit"
                              onClick={() => handleEditTask(task)}
                              title="Edit Task"
                              style={{
                                width: "26px",
                                height: "26px",
                                border: "1px solid #E8ECF2",
                                borderRadius: "6px",
                                background: "#F8F9FC",
                                color: "#6B7280",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                padding: 0,
                                fontSize: "13px",
                              }}
                            >
                              <HiOutlinePencil />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn-icon-delete"
                              onClick={() => handleDeleteTask(task.actionId)}
                              title="Delete Task"
                              style={{
                                width: "26px",
                                height: "26px",
                                border: "1px solid #FEE2E2",
                                borderRadius: "6px",
                                background: "#FEF2F2",
                                color: "#EF4444",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                padding: 0,
                                fontSize: "13px",
                              }}
                            >
                              <HiOutlineTrash />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Meta Fields */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "11px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "12px",
                            color: "#6B7280",
                          }}
                        >
                          <HiOutlineClipboardDocumentCheck style={{ width: "13px", height: "13px", flexShrink: 0 }} />
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {getCapaTitle(task.capaId)}
                          </span>
                        </div>

                        {task.assignedTo && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "12px",
                              color: "#6B7280",
                            }}
                          >
                            <HiOutlineUser style={{ width: "13px", height: "13px", flexShrink: 0 }} />
                            <span>{getUserName(task.assignedTo)}</span>
                          </div>
                        )}

                        {task.dueDate && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "12px",
                              color: "#6B7280",
                            }}
                          >
                            <HiOutlineClock style={{ width: "13px", height: "13px", flexShrink: 0 }} />
                            <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      {/* Card Footer: priority badge + status select */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          paddingTop: "10px", width: '100%',
                          borderTop: "1px solid #F1F3F8",
                        }}
                      >
                        <span
                          className={`priority-badge ${getPriorityColor(task.priority)}`}
                          style={{
                            fontSize: "10px",
                            fontWeight: "600",
                            padding: "3px 9px",
                            borderRadius: "5px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            ...priorityBadgeStyle(task.priority),
                          }}
                        >
                          {getPriorityLabel(task.priority)}
                        </span>

                        <select
                          className="status-select-inline task-status-select"
                          value={task.statusId || ""}
                          onChange={(e) =>
                            handleUpdateTaskStatus(task.actionId, parseInt(e.target.value, 10))
                          }
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            background: "#F8F9FC",
                            border: "1px solid #E8ECF2",
                            borderRadius: "6px",
                            padding: "4px 8px", minWidth: 'auto',
                            cursor: "pointer",
                            fontFamily: "inherit", width: 'auto',
                            outline: "none",
                          }}
                        >
                          {statuses.map((status) => (
                            <option key={status.statusId} value={status.statusId}>
                              {status.statusName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    className="empty-column"
                    style={{
                      textAlign: "center",
                      padding: "24px 0",
                      color: "#B0B9CC",
                      fontSize: "12px",
                    }}
                  >
                    <p>No tasks</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* ── END TASK BOARD ── */}

      {/* Task Modal */}
      <SharedModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        title={editingTask ? "Edit Task" : "Add Task"}
        size="lg"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setShowTaskModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn"
              onClick={handleSaveTask}
              disabled={submittingTask}
            >
              {submittingTask
                ? "Saving..."
                : editingTask
                ? "Update Task"
                : "Save Task"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <div className="form-group">
            <label htmlFor="taskCapaId">
              CAPA <span className="required">*</span>
            </label>
            <select
              id="taskCapaId"
              name="capaId"
              value={taskFormData.capaId}
              onChange={handleTaskInputChange}
              className={taskFormErrors.capaId ? "error" : ""}
              disabled={!!editingTask}
            >
              <option value="">Select CAPA</option>
              {capaRecords.map((capa) => (
                <option key={capa.capaId} value={capa.capaId}>
                  {capa.title || `CAPA ${capa.capaId}`}
                </option>
              ))}
            </select>
            {taskFormErrors.capaId && (
              <span className="field-error">{taskFormErrors.capaId}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="taskDescription">
              Task Description <span className="required">*</span>
            </label>
            <textarea
              id="taskDescription"
              name="taskDescription"
              value={taskFormData.taskDescription}
              onChange={handleTaskInputChange}
              className={taskFormErrors.taskDescription ? "error" : ""}
              rows={3}
              placeholder="Enter task description"
            />
            {taskFormErrors.taskDescription && (
              <span className="field-error">
                {taskFormErrors.taskDescription}
              </span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskAssignedTo">Assigned To</label>
              <select
                id="taskAssignedTo"
                name="assignedTo"
                value={taskFormData.assignedTo}
                onChange={handleTaskInputChange}
              >
                <option value="">Select User (Optional)</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.userName ||
                      user.fullName ||
                      `User ${user.userId}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="taskDueDate">Due Date</label>
              <input
                type="date"
                id="taskDueDate"
                name="dueDate"
                value={taskFormData.dueDate}
                onChange={handleTaskInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="taskStatusId">
                Status <span className="required">*</span>
              </label>
              <select
                id="taskStatusId"
                name="statusId"
                value={taskFormData.statusId}
                onChange={handleTaskInputChange}
                className={taskFormErrors.statusId ? "error" : ""}
                disabled={statuses.length === 0}
              >
                <option value="">
                  {statuses.length === 0
                    ? "No statuses available"
                    : "Select Status"}
                </option>
                {statuses.map((status) => (
                  <option key={status.statusId} value={status.statusId}>
                    {status.statusName || `Status ${status.statusId}`}
                  </option>
                ))}
              </select>
              {taskFormErrors.statusId && (
                <span className="field-error">{taskFormErrors.statusId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="taskPriority">Priority</label>
              <select
                id="taskPriority"
                name="priority"
                value={taskFormData.priority}
                onChange={handleTaskInputChange}
              >
                <option value="1">High</option>
                <option value="2">Medium</option>
                <option value="3">Low</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="taskNotes">Notes</label>
            <textarea
              id="taskNotes"
              name="notes"
              value={taskFormData.notes}
              onChange={handleTaskInputChange}
              rows={3}
              placeholder="Enter task notes"
            />
          </div>
        </div>
      </SharedModal>
    </div>
  );
};

export default CAPATaskManagement;